import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { IntlShape, useIntl } from 'react-intl';
import * as z from 'zod';

import { T } from '@/components/i18n/message.i18n.component';
import Button from '@/components/ui/button/button.component';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form/form.component';
import { Input } from '@/components/ui/input/input.component';
import { Textarea } from '@/components/ui/textarea/textarea.component';
import { CreateProductInput, CreateProductMutation, Product, UpdateProductInput } from '@/gql/graphql';
import { useToast } from '@/hooks/use-toast.hook';
import { useDialogStore } from '@/store/dialog.store';
import { FetchResult, useMutation } from '@apollo/client';
import { formValidationMessages, productFormTranslations, toastTranslations } from './products.messages';
import { allProducts, createProduct, updateProduct } from './products.requests';

function formSchema(t: IntlShape) {
  const chineseCharPattern = /[\u4e00-\u9fff]/;

  return z.object({
    name: z
      .string()
      .transform((value) => value.trim())
      .pipe(
        z
          .string()
          .min(1, {
            message: t.formatMessage(formValidationMessages.minimumLength, {
              minLength: 1,
            }),
          })
          .max(60, {
            message: t.formatMessage(formValidationMessages.maximumLength, {
              maxLength: 60,
            }),
          }),
      )
      .refine(
        (value) => {
          if (!value) return true;
          return !chineseCharPattern.test(value);
        },
        {
          message: t.formatMessage(formValidationMessages.invalidCharacters),
        },
      ),
    description: z
      .string()
      .transform((value) => value.trim())
      .pipe(
        z
          .string()
          .min(1, {
            message: t.formatMessage(formValidationMessages.minimumLength, {
              minLength: 1,
            }),
          })
          .max(260, {
            message: t.formatMessage(formValidationMessages.maximumLength, {
              maxLength: 260,
            }),
          }),
      )
      .refine(
        (value) => {
          if (!value) return true;
          return !chineseCharPattern.test(value);
        },
        {
          message: t.formatMessage(formValidationMessages.invalidCharacters),
        },
      ),
    version: z
      .string()
      .transform((value) => value.trim())
      .pipe(
        z
          .string()
          .min(1, {
            message: t.formatMessage(formValidationMessages.minimumLength, {
              minLength: 1,
            }),
          })
          .max(60, {
            message: t.formatMessage(formValidationMessages.maximumLength, {
              maxLength: 60,
            }),
          }),
      )
      .refine(
        (value) => {
          if (!value) return true;
          return !chineseCharPattern.test(value);
        },
        {
          message: t.formatMessage(formValidationMessages.invalidCharacters),
        },
      ),
    productIdentifier: z
      .string()
      .optional()
      .transform((value) => value || undefined)
      .refine(
        (value) => {
          if (!value) return true;
          if (value.length > 20) return false;
          return true;
        },
        {
          message: t.formatMessage(formValidationMessages.maximumLength, {
            maxLength: 20,
          }),
        },
      )
      .refine(
        (value) => {
          if (!value) return true;
          return !chineseCharPattern.test(value);
        },
        {
          message: t.formatMessage(formValidationMessages.invalidCharacters),
        },
      ),

    // // Create a Zod schema with a refinement to block Chinese characters
    // const schema = z.string().refine((value) => !chineseCharPattern.test(value), {
    //   message: "Input contains Chinese characters, which are not allowed.",
    // });
    projectIdentifier: z
      .string()
      .optional()
      .transform((value) => value || undefined)
      .refine(
        (value) => {
          if (!value) return true;
          if (value.length > 20) return false;
          return true;
        },
        {
          message: t.formatMessage(formValidationMessages.maximumLength, {
            maxLength: 20,
          }),
        },
      )
      .refine(
        (value) => {
          if (!value) return true;
          return !chineseCharPattern.test(value);
        },
        {
          message: t.formatMessage(formValidationMessages.invalidCharacters),
        },
      ),
  });
}

type ProductFormProps = {
  edit?: boolean;
  initialValues?: Product;
  handleSubmit?: (values: CreateProductInput | UpdateProductInput) => void;
  afterAdd?: (result: FetchResult<CreateProductMutation>) => void;
};

export const ProductForm = ({ initialValues, edit, afterAdd }: ProductFormProps) => {
  const intl = useIntl();
  const { toast } = useToast();

  const { handleDialogClose } = useDialogStore((state) => ({
    handleDialogClose: state.closeDialog,
  }));

  const [createProductMutation, { loading: addProductLoading }] = useMutation(createProduct);

  const [editProductMutation, { loading: editProductLoading }] = useMutation(updateProduct);

  const form = useForm<z.infer<ReturnType<typeof formSchema>>>({
    resolver: zodResolver(formSchema(intl)),
    defaultValues: {
      name: initialValues?.name,
      description: initialValues?.description,
      version: initialValues?.version,
      productIdentifier: initialValues?.productIdentifier || undefined,
      projectIdentifier: initialValues?.projectIdentifier || undefined,
    } || {
      name: '',
      description: '',
      productType: '',
      version: '',
      productIdentifier: '',
      projectIdentifier: '',
    },
  });

  async function onSubmit(values: z.infer<ReturnType<typeof formSchema>>) {
    if (edit) {
      if (!initialValues) return;
      await editProductMutation({
        variables: {
          updateProductInput: {
            id: initialValues.id,
            name: values.name,
            description: values.description,
            lifecycleStatus: initialValues.lifecycleStatus,
            version: values.version,
            productIdentifier: values.productIdentifier || '',
            projectIdentifier: values.projectIdentifier || '',
          },
        },
        refetchQueries: [{ query: allProducts }],
      });
      handleDialogClose();
      toast({
        description: intl.formatMessage(toastTranslations.editProductToast),
        variant: 'default',
      });
      return;
    }
    const data = await createProductMutation({
      variables: {
        createProductInput: {
          name: values.name,
          description: values.description,
          version: values.version,
          productIdentifier: values.productIdentifier || '',
          projectIdentifier: values.projectIdentifier || '',
        },
      },
      refetchQueries: [{ query: allProducts }],
    });
    handleDialogClose();
    toast({
      description: intl.formatMessage(toastTranslations.addProductToast),
      variant: 'default',
    });
    if (afterAdd) afterAdd(data);
  }

  return (
    <Form {...form} data-testid="product-form-container">
      <form
        data-testid="product-form-form"
        onSubmit={form.handleSubmit(onSubmit)}
        className="space-y-4 active:border-none"
      >
        <FormField
          control={form.control}
          name="name"
          // eslint-disable-next-line react/jsx-handler-names
          render={({ field }) => (
            <FormItem>
              <div className="flex flex-row justify-between items-center mb-3 ">
                <FormLabel data-testid="product-form-label-name" required>
                  <T {...productFormTranslations.nameInputLabel} />
                </FormLabel>
                <FormMessage data-testid="product-form-error-name" />
              </div>
              <FormControl>
                <Input
                  data-testid="product-form-field-name"
                  placeholder={intl.formatMessage(productFormTranslations.nameInputPlaceholder)}
                  {...field}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="description"
          // eslint-disable-next-line react/jsx-handler-names
          render={({ field }) => (
            <FormItem>
              <div className="flex flex-row justify-between items-center mb-3 ">
                <FormLabel data-testid="product-form-label-description" required>
                  <T {...productFormTranslations.descriptionInputLabel} />
                </FormLabel>
                <FormMessage data-testid="product-form-error-description" />
              </div>
              <FormControl>
                <Textarea
                  data-testid="product-form-field-description"
                  placeholder={intl.formatMessage(productFormTranslations.descriptionInputPlaceholder)}
                  {...field}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="version"
          // eslint-disable-next-line react/jsx-handler-names
          render={({ field }) => (
            <FormItem>
              <div className="flex flex-row justify-between items-center mb-3 ">
                <FormLabel data-testid="product-form-label-version" required>
                  <T {...productFormTranslations.productVersionInputLabel} />
                </FormLabel>
                <FormMessage data-testid="product-form-error-version" />
              </div>
              <FormControl>
                <Input
                  data-testid="product-form-field-version"
                  {...field}
                  placeholder={intl.formatMessage(productFormTranslations.productVersionInputPlaceholder)}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="productIdentifier"
          // eslint-disable-next-line react/jsx-handler-names
          render={({ field }) => (
            <FormItem>
              <div className="flex flex-row justify-between items-center mb-3 ">
                <FormLabel data-testid="product-form-label-product-identifier">
                  <T {...productFormTranslations.productProductIdentifierInputLabel} />
                </FormLabel>
                <FormMessage data-testid="product-form-error-product-identifier" />
              </div>
              <FormControl>
                <Input
                  data-testid="product-form-field-product-identifier"
                  {...field}
                  placeholder={intl.formatMessage(productFormTranslations.productProductIdentifierInputPlaceholder)}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="projectIdentifier"
          // eslint-disable-next-line react/jsx-handler-names
          render={({ field }) => (
            <FormItem>
              <div className="flex flex-row justify-between items-center mb-3 ">
                <FormLabel data-testid="product-form-label-project-identifier">
                  <T {...productFormTranslations.productProjectIdentifierInputLabel} />
                </FormLabel>
                <FormMessage data-testid="product-form-error-project-identifier" />
              </div>
              <FormControl>
                <Input
                  data-testid="product-form-field-project-identifier"
                  {...field}
                  placeholder={intl.formatMessage(productFormTranslations.productProjectIdentifierInputPlaceholder)}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <div className="flex flex-row justify-between">
          <Button
            data-testid="product-form-button-cancel"
            disabled={addProductLoading || editProductLoading}
            onClick={handleDialogClose}
            type="button"
            variant="secondary"
          >
            <T {...productFormTranslations.backButton} />
          </Button>
          <Button
            data-testid="product-form-button-add"
            loading={addProductLoading || editProductLoading}
            disabled={!form.formState.isDirty}
            type="submit"
            variant="default"
          >
            <T {...productFormTranslations.sumbitButton} />
          </Button>
        </div>
      </form>
    </Form>
  );
};
